import { Component, Input, Output, EventEmitter, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { Observable, of, Subject, throwError } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Deal } from 'ng2-app/models/dto/deal.model';
import { FormlyDialogComponent, FormlyDialogData } from 'ng2-app/components/formly-dialog/formly-dialog.component';
import { DataListItem } from '../../../borrower-overview/data-list/data-list.component';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { BorrowerValueService } from 'ng2-app/services/borrower-value/borrower-value.service';
import { UntypedFormGroup } from '@angular/forms';
import { DealApplicationStatusHelperService } from './../deal-application-status-helper.service';
import { QuestionsListStatus } from 'app/models/dto/question.model';
import { FormlyModel } from 'app/form-core/formly/formly-core.config';
import { UpdateAnswers } from 'app/store/deal/deal-application-requirements/deal-application-requirement.actions';
import { Store } from '@ngxs/store';
import { take } from 'rxjs/operators';


@Component({
  selector: 'app-deal-app-status-smart',
  templateUrl: './deal-app-status-smart.component.html',
  styleUrls: ['./deal-app-status-smart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DealAppStatusSmartComponent implements OnChanges {

  @Input() deal: Deal;
  @Input() borrowerId: number;
  @Input() loadingStatus = true;
  @Input() questionListStatus: QuestionsListStatus;
  @Output() change = new EventEmitter();
  percentComplete = 0;
  showEditLink = true;
  destroyed$ = new Subject<boolean>();
  dataList$: Observable<DataListItem[]>;
  labels$: Observable<string[]>;
  formlyFields: FormlyFieldConfig[];
  formlyModel: FormlyModel;
  private originalModel: FormlyModel;
  private dialogRef: MatDialogRef<FormlyDialogComponent>;


  constructor(
    public dialog: MatDialog,
    private borrowerValueService: BorrowerValueService,
    private store: Store,
    private dealApplicationStatusHelperService: DealApplicationStatusHelperService
  ) { }

  ngOnChanges() {
    if (this.questionListStatus) {
      this.percentComplete = this.questionListStatus.percentComplete;
      if (this.questionListStatus.fulfilledCount === 0 && this.questionListStatus.requiredCount === 0) {
        this.showEditLink = false;
      }
    }
  }

  private formlyDialogData(): FormlyDialogData {
    const title = `${this.deal.loan_product_lender.name} ${this.deal.loan_product.name}`;
    const fields = this.dealApplicationStatusHelperService.getModifiedFieldswithCSSApplied(this.formlyFields);

    return {
      loading$: of(false),
      title,
      errorMessage: '',
      formlyConfig: {
        model: this.formlyModel,
        form: new UntypedFormGroup({}),
        fields,
        options: {}
      }
    };
  }

  /* tslint:disable:no-unused-variable  */
  private openQuestionDialog() {
    const groupedItems = this.dealApplicationStatusHelperService.getGroupedFormlyFields(this.questionListStatus.application);
    const borrowerValues = groupedItems.filter(borrowerValue => borrowerValue.type !== 'document');
    // change the dot to hyphen to conform with angular formly standards
    const modifiedBorrowerValues = this.dealApplicationStatusHelperService.convertKeyValuestoHypenFormat(borrowerValues);
    this.formlyModel = modifiedBorrowerValues.reduce((collect, borrowerValue) => {
      const value = borrowerValue.type === 'multiselect' ? borrowerValue?.value?.split(',') || [] : borrowerValue?.value;
      collect[borrowerValue.alias] = value;
      return collect;
    }, {});
    this.originalModel = Object.assign({}, this.formlyModel);
    const formlyFields = this.borrowerValueService.borrowerValuesToFormlyFieldConfigs(this.formlyModel, modifiedBorrowerValues);

    this.formlyFields =
      this.dealApplicationStatusHelperService.modifyFormlyFields(formlyFields, this.questionListStatus.application);

    // add required property to fields which have values
    Object.keys(this.formlyModel).forEach(key => {
      // if the field doesnot have a value
      if (!this.formlyModel[key]) {
        const reqObj = this.formlyFields.find(el => el.key === key);
        if (reqObj && reqObj.templateOptions) {
          if (!reqObj.className?.includes('required')) {
            reqObj.className = `${reqObj.className} required`;
          }
        }
      }
      if (this.formlyModel[key]) {
        const reqObj = this.formlyFields.find(el => el.key === key);
        if (reqObj && reqObj.templateOptions) {
          reqObj.templateOptions.required = true;
          reqObj.className = reqObj.className?.replace('required', '');
        }
      }
    });

    this.formlyModel.sectionTitle = '';
    this.dialogRef = this.dialog.open(FormlyDialogComponent, {
      data: this.formlyDialogData(),
      width: '50rem',
      panelClass: 'question-dialog-container',
      maxHeight: '100vh',
    });

    this.dialogRef.componentInstance.saveData.subscribe(result => {
      this.dialogRef.componentInstance.data.errorMessage = '';
      this.dialogRef.componentInstance.data.loading$ = of(true);
      // tslint:disable-next-line: max-line-length
      const modifiedModelChanges = this.dealApplicationStatusHelperService
        .getDiffProperties(this.formlyFields, this.originalModel, this.formlyModel);
      this.store.dispatch(new UpdateAnswers(this.deal, modifiedModelChanges)).pipe(take(1)).subscribe({
        next: () => {
          if (!this.dialogRef || !this.dialogRef.componentInstance) {
            return;
          }
          this.dialogRef.componentInstance.data.loading$ = of(false);
          this.formlyModel = result.formlyConfig.model;
          this.dialogRef.close();
        },
        error: (error) => {
          if (!this.dialogRef || !this.dialogRef.componentInstance) {
            return;
          }
          this.dialogRef.componentInstance.data.loading$ = of(false);
          if (error.message) {
            this.dialogRef.componentInstance.data.errorMessage = error.message;
          } else {
            this.dialogRef.componentInstance.data.errorMessage = 'Failed to update borrower info';
          }
        }});
    });
  }
}
